import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SuppliersContent } from './SuppliersPage';
import { AccountsContent } from './AccountsPage';
import { FinancialSummary } from '../components/financial/FinancialSummary';
import { FixedExpensesList } from '../components/financial/FixedExpensesList';
import axios from 'axios';
import { Plus, FileText, Trash2, Truck, ShoppingBag, Wallet, Tag, BarChart3, Receipt, Eye, X, Package } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { QuickProductForm, categoryLabelOf } from '../components/QuickProductForm';
import { useProductCategories } from '../lib/categories';
import { useCompanySettings } from '../hooks/useCompanySettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURCHASE_CATEGORIES = [
  { value: 'acougue', label: 'Açougue', color: 'bg-red-100 text-red-700' },
  { value: 'mercearia', label: 'Mercearia', color: 'bg-amber-100 text-amber-700' },
  { value: 'flv', label: 'FLV (Frutas, Legumes e Verduras)', color: 'bg-green-100 text-green-700' },
  { value: 'padaria', label: 'Padaria', color: 'bg-orange-100 text-orange-700' },
  { value: 'bebidas', label: 'Bebidas', color: 'bg-blue-100 text-blue-700' },
  { value: 'frios_laticinios', label: 'Frios e Laticínios', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'limpeza', label: 'Limpeza', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'higiene', label: 'Higiene e Perfumaria', color: 'bg-fuchsia-100 text-fuchsia-700' },
  { value: 'outros', label: 'Outros', color: 'bg-slate-100 text-slate-700' },
];

const categoryLabel = (v) => PURCHASE_CATEGORIES.find(c => c.value === v)?.label || (v || '—');
const PAYMENT_METHODS = [
  { value: 'boleto', label: 'Boleto (a prazo → gera Conta a Pagar)' },
  { value: 'dinheiro', label: 'Dinheiro (pago à vista)' },
  { value: 'pix', label: 'PIX (pago à vista)' },
  { value: 'cartao', label: 'Cartão (pago à vista)' },
  { value: 'transferencia', label: 'Transferência (pago à vista)' },
  { value: 'cheque', label: 'Cheque' },
];
const paymentLabel = (v) => ({ boleto: 'Boleto', dinheiro: 'Dinheiro', pix: 'PIX', cartao: 'Cartão', transferencia: 'Transferência', cheque: 'Cheque' }[v] || null);
const EMPTY_FORM = () => ({
  supplier_id: '', supplier_name: '', note_number: '',
  issue_date: new Date().toISOString().substring(0, 10),
  payment_term: '', payment_method: '', create_payable: true,
  due_date: '', notes: '', category: ''
});
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }); } catch { return '—'; } };
const categoryColor = (v) => PURCHASE_CATEGORIES.find(c => c.value === v)?.color || 'bg-slate-100 text-slate-700';

export const PurchaseNotesPage = () => {
  const [mainTab, setMainTab] = useState('resumo');
  const [notes, setNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [summaryByCategory, setSummaryByCategory] = useState([]);
  const [viewNote, setViewNote] = useState(null);
  const [success, setSuccess] = useState('');
  const [barcode, setBarcode] = useState('');
  const [barcodeMsg, setBarcodeMsg] = useState('');
  const qtyRef = React.useRef(null);

  const [formData, setFormData] = useState(EMPTY_FORM());

  const [itemForm, setItemForm] = useState({
    product_id: '', product_name: '', quantity: '', unit_cost: '', category: '', sale_price: '', ncm: '', cest: ''
  });
  const companySettings = useCompanySettings();
  const PRODUCT_CATEGORIES = useProductCategories();
  const suggestedPrice = (() => {
    const cost = parseFloat(itemForm.unit_cost);
    const margin = companySettings?.category_margins?.[itemForm.category];
    if (!(cost > 0) || !(margin > 0)) return null;
    return { price: Math.round(cost * (1 + margin / 100) * 100) / 100, margin };
  })();
  useEffect(() => {
    if (suggestedPrice && itemForm.sale_price === '') setItemForm((f) => ({ ...f, sale_price: String(suggestedPrice.price) }));
  }, [suggestedPrice?.price]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [nRes, sRes, pRes, sumRes] = await Promise.all([
        axios.get(`${API}/purchase-notes`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/purchase-notes/summary/by-category?days=30`, { withCredentials: true })
      ]);
      setNotes(nRes.data);
      setSuppliers(sRes.data);
      setProducts(pRes.data);
      setSummaryByCategory(sumRes.data || []);
    } catch (err) { console.error(err); }
  };

  const [reversing, setReversing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const openEdit = (note) => {
    setEditingId(note.id);
    setFormData({
      supplier_id: note.supplier_id || '',
      supplier_name: note.supplier_name || '',
      note_number: note.note_number || '',
      issue_date: (note.issue_date || new Date().toISOString()).substring(0, 10),
      payment_term: note.payment_term || '',
      payment_method: note.payment_method || '',
      create_payable: false,
      due_date: note.due_date ? String(note.due_date).substring(0, 10) : '',
      notes: note.notes || '',
      category: note.category || ''
    });
    setItems((note.items || []).map(it => ({
      product_id: it.product_id || null,
      product_name: it.product_name,
      code: it.code || null,
      quantity: it.quantity,
      unit_cost: it.unit_cost,
      subtotal: it.subtotal
    })));
    setViewNote(null);
    setShowForm(true);
  };

  const reverseNote = async (note) => {
    if (!note || note.status === 'estornada') return;
    setReversing(true);
    try {
      const res = await axios.post(`${API}/purchase-notes/${note.id}/reverse`, {}, { withCredentials: true });
      await load();
      setViewNote(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao estornar nota');
    } finally {
      setReversing(false);
    }
  };

  const [quickCreate, setQuickCreate] = useState(null); // código não encontrado → cadastro rápido

  const lookupBarcode = async () => {
    const code = barcode.trim();
    if (!code) return;
    setBarcodeMsg('');
    setQuickCreate(null);
    try {
      const { data } = await axios.get(`${API}/products/code/${encodeURIComponent(code)}`, { withCredentials: true });
      setItemForm({ product_id: data.id, product_name: data.name, quantity: '', unit_cost: data.cost_price ? String(data.cost_price) : '', category: data.category || '', sale_price: data.price ? String(data.price) : '', ncm: data.ncm || '', cest: data.cest || '' });
      setBarcodeMsg(`✓ ${data.name} · ${categoryLabelOf(data.category)} · estoque atual ${Number(data.stock_quantity || 0)}`);
      setTimeout(() => qtyRef.current?.focus(), 50);
    } catch (err) {
      setBarcodeMsg(`Código ${code} não encontrado — cadastre o produto abaixo ou lance como avulso`);
      setQuickCreate(code);
    }
  };

  const onQuickCreated = (p) => {
    setProducts((prev) => [...prev, p]);
    setItemForm({ product_id: p.id, product_name: p.name, quantity: '', unit_cost: p.cost_price ? String(p.cost_price) : '', category: p.category || '', sale_price: p.price ? String(p.price) : '', ncm: p.ncm || '', cest: p.cest || '' });
    setBarcodeMsg(`✓ ${p.name} cadastrado · ${categoryLabelOf(p.category)} · estoque atual 0`);
    setQuickCreate(null);
    setTimeout(() => qtyRef.current?.focus(), 50);
  };

  const addItem = () => {
    if (!itemForm.product_id || !itemForm.product_name || !itemForm.quantity || !itemForm.unit_cost) {
      setError('Preencha produto, quantidade e custo unitário');
      return;
    }
    const qty = parseFloat(itemForm.quantity);
    const cost = parseFloat(itemForm.unit_cost);
    const subtotal = qty * cost;
    const prod = products.find(x => x.id === itemForm.product_id);
    const salePrice = parseFloat(itemForm.sale_price);
    if (salePrice > 0 && salePrice < cost && !window.confirm('O preço de venda está abaixo do custo. Adicionar mesmo assim?')) return;
    // Custo, preço de venda e categoria são gravados no cadastro do produto pelo backend AO SALVAR a entrada.
    setItems([...items, {
      product_id: itemForm.product_id || null,
      product_name: itemForm.product_name,
      code: prod?.barcode || prod?.code || barcode.trim() || null,
      category: itemForm.category || prod?.category || null,
      unit: prod?.unit || null,
      sale_price: salePrice > 0 ? salePrice : null,
      ncm: itemForm.ncm || prod?.ncm || null,
      cest: itemForm.cest || prod?.cest || null,
      quantity: qty,
      unit_cost: cost,
      subtotal
    }]);
    setItemForm({ product_id: '', product_name: '', quantity: '', unit_cost: '', category: '', sale_price: '', ncm: '', cest: '' });
    setBarcode('');
    setBarcodeMsg('');
    setQuickCreate(null);
    setError('');
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };
  const updateItemField = (idx, field, value) => setItems(items.map((it, k) => (k === idx ? { ...it, [field]: value || null } : it)));

  const [savingCatIdx, setSavingCatIdx] = useState(null);
  const changeItemCategory = async (idx, category) => {
    const it = items[idx];
    setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, category: category || null } : x)));
    if (!it.product_id || !category) return;
    setSavingCatIdx(idx);
    try {
      await axios.put(`${API}/products/${it.product_id}`, { category }, { withCredentials: true });
      setProducts((prev) => prev.map((p) => (p.id === it.product_id ? { ...p, category } : p)));
      setSuccess(`Categoria de "${it.product_name}" alterada para ${categoryLabelOf(category)}.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao alterar a categoria do produto');
    } finally {
      setSavingCatIdx(null);
    }
  };

  const totalNote = items.reduce((s, i) => s + i.subtotal, 0);
  const isBoleto = formData.payment_method === 'boleto';
  const linkedCount = items.filter(i => i.product_id).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      setError('Adicione ao menos um item');
      return;
    }
    if (!editingId && !formData.payment_method) {
      setError('Selecione a forma de pagamento da nota');
      return;
    }
    if (isBoleto && !formData.due_date) {
      setError('Informe a data de vencimento do boleto');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const supplierName = formData.supplier_name || suppliers.find(s => s.id === formData.supplier_id)?.name || 'Avulso';
      const payload = {
        supplier_id: formData.supplier_id || null,
        supplier_name: supplierName,
        note_number: formData.note_number,
        items,
        total: parseFloat(totalNote.toFixed(2)),
        issue_date: new Date(formData.issue_date).toISOString(),
        payment_term: formData.payment_term || null,
        payment_method: formData.payment_method || null,
        create_payable: isBoleto,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        notes: formData.notes,
        category: formData.category || null
      };
      if (editingId) {
        await axios.put(`${API}/purchase-notes/${editingId}`, payload, { withCredentials: true });
        setSuccess(`Nota #${formData.note_number} atualizada.`);
      } else {
        const { data } = await axios.post(`${API}/purchase-notes`, payload, { withCredentials: true });
        const parts = [`estoque atualizado em ${data.stock_updated_count} produto(s)`];
        if (data.payable_id) parts.push(`conta a pagar gerada (venc. ${fmtDate(data.due_date)})`);
        else parts.push('paga à vista — sem conta a pagar');
        if (data.unlinked_items?.length) parts.push(`${data.unlinked_items.length} item(ns) avulso(s) sem vínculo de estoque: ${data.unlinked_items.join(', ')}`);
        setSuccess(`Nota #${data.note_number} lançada: ${parts.join(' · ')}.`);
      }
      setTimeout(() => setSuccess(''), 10000);
      load();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar nota');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM());
    setItems([]);
    setBarcode('');
    setBarcodeMsg('');
    setQuickCreate(null);
    setItemForm({ product_id: '', product_name: '', quantity: '', unit_cost: '', category: '', sale_price: '', ncm: '', cest: '' });
    setShowForm(false);
    setEditingId(null);
    setError('');
  };

  return (
    <Layout>
      <div className="p-8" data-testid="purchase-notes-page">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Gestão Financeira</h1>
        <p className="text-sm text-slate-600 mb-4">Resumo de caixa, compras de mercadorias, despesas fixas, contas a pagar e fornecedores</p>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 h-auto gap-1 mb-6">
            <TabsTrigger value="resumo" data-testid="tab-financial-summary">
              <BarChart3 size={16} className="mr-2" /> Resumo Financeiro
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-purchase-notes">
              <ShoppingBag size={16} className="mr-2" /> Compras de Mercadorias
            </TabsTrigger>
            <TabsTrigger value="despesas" data-testid="tab-fixed-expenses">
              <Receipt size={16} className="mr-2" /> Despesas Fixas
            </TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-payables">
              <Wallet size={16} className="mr-2" /> Contas a Pagar
            </TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers">
              <Truck size={16} className="mr-2" /> Fornecedores
            </TabsTrigger>
          </TabsList>

          <TabsContent value="resumo">
            <FinancialSummary />
          </TabsContent>

          <TabsContent value="despesas">
            <FixedExpensesList />
          </TabsContent>

          <TabsContent value="notes">
            {/* Resumo por categoria (últimos 30 dias) */}
            {summaryByCategory.length > 0 && (
              <div className="bg-white rounded-md border border-slate-200 p-4 mb-4" data-testid="purchases-by-category">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="text-blue-600" size={18} />
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Compras por categoria · últimos 30 dias</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {summaryByCategory.map((c) => (
                    <div key={c.category} className={`rounded-md px-3 py-2 ${categoryColor(c.category)} border border-current/20`} data-testid={`cat-summary-${c.category}`}>
                      <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">{categoryLabel(c.category).split(' ')[0]}</p>
                      <p className="text-lg font-black">R$ {Number(c.total).toFixed(2)}</p>
                      <p className="text-[10px] opacity-75">{c.count} nota(s)</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {success && (
              <Alert className="mb-4 border-emerald-300 bg-emerald-50 text-emerald-900" data-testid="note-success-alert">
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-end mb-4">
              <Button onClick={() => { if (showForm) { resetForm(); } else { setEditingId(null); setItems([]); setFormData(EMPTY_FORM()); setShowForm(true); } }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-note-btn">
                <Plus size={20} className="mr-2" /> Nova Nota de Entrada
              </Button>
            </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingId ? `Editar Nota #${formData.note_number}` : 'Lançar Nota de Entrada'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Fornecedor *</Label>
                  <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v, supplier_name: suppliers.find(s => s.id === v)?.name || '' })}>
                    <SelectTrigger><SelectValue placeholder="Selecione um fornecedor" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Ou digite o nome (avulso)"
                    data-testid="note-supplier-name-input"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número da NF *</Label>
                  <Input value={formData.note_number} onChange={(e) => setFormData({ ...formData, note_number: e.target.value })} required data-testid="note-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="note-category-select"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                    <SelectContent>
                      {PURCHASE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data Emissão *</Label>
                  <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} required data-testid="note-issue-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Cond. Pagamento</Label>
                  <Input value={formData.payment_term} onChange={(e) => setFormData({ ...formData, payment_term: e.target.value })} placeholder="Ex: 30 dias" />
                </div>
                <div className="space-y-2">
                  <Label>Forma de Pagamento *</Label>
                  <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                    <SelectTrigger data-testid="note-payment-method-select"><SelectValue placeholder="Selecione a forma" /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value} data-testid={`note-payment-method-${m.value}`}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {isBoleto && (
                  <div className="space-y-2">
                    <Label>Vencimento do Boleto *</Label>
                    <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} required data-testid="note-due-date-input" />
                  </div>
                )}
              </div>

              {!editingId && formData.payment_method && (
                <div className={`rounded-md border px-3 py-2 text-xs ${isBoleto ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-emerald-50 border-emerald-300 text-emerald-900'}`} data-testid="note-automation-info">
                  {isBoleto
                    ? <>Ao lançar: <b>entrada automática no estoque</b> dos itens vinculados + <b>Conta a Pagar gerada automaticamente</b> com vencimento em <b>{formData.due_date ? fmtDate(formData.due_date) : '(informe a data)'}</b>.</>
                    : <>Ao lançar: <b>entrada automática no estoque</b> dos itens vinculados. Nota paga à vista ({paymentLabel(formData.payment_method)}) — <b>não gera conta a pagar</b>.</>}
                </div>
              )}
              {editingId && (
                <p className="text-xs text-slate-500">Editar ajusta o estoque pela diferença dos itens e atualiza a conta a pagar vinculada (se houver).</p>
              )}

              {/* Add items */}
              <div className="border-t border-slate-200 pt-4">
                <h3 className="font-bold text-slate-900 mb-1">Itens da Nota</h3>
                <p className="text-xs text-slate-500 mb-3">Bipe ou digite o código de barras e pressione Enter — o produto é localizado e a quantidade entra no estoque ao lançar a nota.</p>
                <div className="grid grid-cols-1 md:grid-cols-[repeat(16,minmax(0,1fr))] gap-2 mb-1">
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">Código de barras</Label>
                    <Input
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupBarcode(); } }}
                      onBlur={() => { if (barcode.trim() && !itemForm.product_id) lookupBarcode(); }}
                      placeholder="Bipe o código"
                      autoComplete="off"
                      data-testid="item-barcode-input"
                    />
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">Produto</Label>
                    <Select value={itemForm.product_id} onValueChange={(v) => {
                      if (!v) return; // Radix dispara '' quando o item recém-cadastrado ainda não está registrado
                      const p = products.find(x => x.id === v);
                      setItemForm({ ...itemForm, product_id: v, product_name: p?.name || '', unit_cost: p?.cost_price?.toString() || '', category: p?.category || '', sale_price: p?.price?.toString() || '', ncm: p?.ncm || '', cest: p?.cest || '' });
                      setBarcode(p?.barcode || '');
                      setBarcodeMsg('');
                    }}>
                      <SelectTrigger data-testid="item-product-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {products.map(p => <SelectItem key={p.id} value={p.id} data-testid={`item-product-option-${p.id}`}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1 space-y-1">
                    <Label className="text-xs">NCM</Label>
                    <Input value={itemForm.ncm} onChange={(e) => setItemForm({ ...itemForm, ncm: e.target.value.replace(/\D/g, '').slice(0, 8) })} placeholder="8 dígitos" maxLength={8} className="font-mono" data-testid="item-ncm-input" />
                  </div>
                  <div className="md:col-span-1 space-y-1">
                    <Label className="text-xs">CEST <span className="text-slate-400">(opc.)</span></Label>
                    <Input value={itemForm.cest} onChange={(e) => setItemForm({ ...itemForm, cest: e.target.value.replace(/\D/g, '').slice(0, 7) })} placeholder="7 dígitos" maxLength={7} className="font-mono" data-testid="item-cest-input" />
                  </div>
                  <div className="md:col-span-1 space-y-1">
                    <Label className="text-xs">Qtd</Label>
                    <Input ref={qtyRef} type="number" step="0.01" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} data-testid="item-quantity-input" />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">Categoria</Label>
                    <select value={itemForm.category || ''} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                      className={`w-full h-10 px-2 rounded-md border border-slate-300 bg-white text-sm ${itemForm.category ? categoryColor(itemForm.category) + ' font-bold' : 'text-slate-500'}`}
                      title={itemForm.product_id ? 'Categoria do produto — pode alterar; será salva no cadastro' : 'Categoria do item avulso'}
                      data-testid="item-category-select">
                      <option value="">{itemForm.product_id ? '—' : 'Selecione'}</option>
                      {PRODUCT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">Custo Unit.</Label>
                    <Input type="number" step="0.01" value={itemForm.unit_cost} onChange={(e) => setItemForm({ ...itemForm, unit_cost: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} data-testid="item-cost-input" />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">Preço de Venda{itemForm.sale_price && itemForm.unit_cost && parseFloat(itemForm.unit_cost) > 0 && (
                      <span className={`ml-1 font-bold ${parseFloat(itemForm.sale_price) >= parseFloat(itemForm.unit_cost) ? 'text-emerald-600' : 'text-red-600'}`} data-testid="item-margin-hint">
                        · {(((parseFloat(itemForm.sale_price) - parseFloat(itemForm.unit_cost)) / parseFloat(itemForm.unit_cost)) * 100).toFixed(0)}%
                      </span>)}</Label>
                    <Input type="number" step="0.01" min="0" value={itemForm.sale_price} onChange={(e) => setItemForm({ ...itemForm, sale_price: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                      placeholder={itemForm.product_id ? '' : 'opcional'} title="Preço de venda do produto — alterar aqui atualiza o cadastro" data-testid="item-sale-price-input" />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button type="button" onClick={addItem} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="add-item-btn">+</Button>
                  </div>
                </div>
                {suggestedPrice && Math.abs(suggestedPrice.price - parseFloat(itemForm.sale_price || 0)) >= 0.01 && (
                  <p className="text-xs mb-2 flex items-center gap-2 text-blue-700" data-testid="price-suggestion">
                    <span>Sugestão de venda: <b>R$ {suggestedPrice.price.toFixed(2)}</b> (margem {categoryLabelOf(itemForm.category).split(' ')[0]} {suggestedPrice.margin}%)</span>
                    <button type="button" onClick={() => setItemForm({ ...itemForm, sale_price: String(suggestedPrice.price) })}
                      className="px-2 py-0.5 rounded bg-blue-600 text-white font-bold hover:bg-blue-700" data-testid="apply-price-suggestion">Aplicar</button>
                  </p>
                )}
                {barcodeMsg && (
                  <p className={`text-xs mb-3 ${barcodeMsg.startsWith('✓') ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="item-barcode-msg">{barcodeMsg}</p>
                )}
                {quickCreate && (
                  <QuickProductForm barcode={quickCreate} onCreated={onQuickCreated} onCancel={() => setQuickCreate(null)} />
                )}

                {items.length > 0 && (
                  <table className="w-full border border-slate-200 rounded" data-testid="note-items-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-bold uppercase">Produto</th>
                        <th className="px-3 py-2 text-left text-xs font-bold uppercase">NCM / CEST</th>
                        <th className="px-3 py-2 text-left text-xs font-bold uppercase">Categoria</th>
                        <th className="px-3 py-2 text-left text-xs font-bold uppercase">Estoque</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Qtd</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Custo</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Venda</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Subtotal</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => (
                        <tr key={idx} className="border-t border-slate-200">
                          <td className="px-3 py-2">{it.product_name}{it.code && <span className="ml-2 text-[10px] font-mono text-slate-400">{it.code}</span>}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <input value={it.ncm || ''} onChange={(e) => updateItemField(idx, 'ncm', e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="NCM" maxLength={8}
                                className={`w-24 h-7 px-1.5 rounded border text-xs font-mono ${it.ncm && it.ncm.length !== 8 ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} title="NCM (8 dígitos) — editável antes de lançar" data-testid={`note-item-ncm-${idx}`} />
                              <input value={it.cest || ''} onChange={(e) => updateItemField(idx, 'cest', e.target.value.replace(/\D/g, '').slice(0, 7))} placeholder="CEST" maxLength={7}
                                className={`w-20 h-7 px-1.5 rounded border text-xs font-mono ${it.cest && it.cest.length !== 7 ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} title="CEST (7 dígitos, opcional)" data-testid={`note-item-cest-${idx}`} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {(() => { const cat = it.category || products.find((p) => p.id === it.product_id)?.category || ''; return (
                              <select value={cat} onChange={(e) => changeItemCategory(idx, e.target.value)} disabled={savingCatIdx === idx}
                                title={it.product_id ? 'Alterar categoria do produto (salva no cadastro)' : 'Categoria do item avulso'}
                                className={`h-7 px-1.5 rounded border border-transparent hover:border-slate-300 font-bold text-xs cursor-pointer ${cat ? categoryColor(cat) : 'bg-slate-100 text-slate-500'}`}
                                data-testid={`note-item-category-${idx}`}>
                                <option value="">—</option>
                                {PRODUCT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>); })()}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {it.product_id
                              ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">+{it.quantity} no estoque</span>
                              : <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold" title="Item avulso: não está vinculado a um produto cadastrado">avulso · sem estoque</span>}
                          </td>
                          <td className="px-3 py-2 text-right">{it.quantity}</td>
                          <td className="px-3 py-2 text-right">R$ {it.unit_cost.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-bold" data-testid={`note-item-sale-price-${idx}`}>{it.sale_price ? `R$ ${Number(it.sale_price).toFixed(2)}` : <span className="text-slate-400 font-normal">—</span>}</td>
                          <td className="px-3 py-2 text-right font-bold">R$ {it.subtotal.toFixed(2)}</td>
                          <td className="px-3 py-2"><button type="button" onClick={() => removeItem(idx)} className="text-red-600"><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-bold">
                        <td colSpan={6} className="px-3 py-2 text-right">TOTAL · {linkedCount}/{items.length} item(ns) com entrada no estoque</td>
                        <td className="px-3 py-2 text-right text-blue-600">R$ {totalNote.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-note-btn">{loading ? 'Salvando...' : (editingId ? 'Salvar Alterações' : 'Lançar Nota')}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Nº NF</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Emissão</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Fornecedor</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Items</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Total</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pagamento</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {notes.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nenhuma nota lançada</td></tr>}
              {notes.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><FileText size={16} className="text-slate-400" /><span className="font-medium">{n.note_number}</span>{n.status === 'estornada' && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold" data-testid={`note-status-${n.id}`}>ESTORNADA</span>}</div></td>
                  <td className="px-4 py-3 text-sm">{fmtDate(n.issue_date)}</td>
                  <td className="px-4 py-3 text-sm font-medium">{n.supplier_name}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`px-2 py-1 rounded font-bold ${categoryColor(n.category)}`}>
                      {categoryLabel(n.category).split(' ')[0]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{n.items?.length || 0} item(s)</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-600">R$ {n.total.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {paymentLabel(n.payment_method) || n.payment_term || '-'}
                    {n.payable_id && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold" data-testid={`note-payable-badge-${n.id}`}>A PAGAR</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      type="button" size="sm" variant="outline"
                      className="h-8 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => setViewNote(n)}
                      data-testid={`view-note-${n.id}`}
                    >
                      <Eye size={14} /> Ver
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </TabsContent>

          <TabsContent value="accounts">
            <AccountsContent showOnly="payables" hideHeader />
          </TabsContent>

          <TabsContent value="suppliers">
            <SuppliersContent />
          </TabsContent>
        </Tabs>
      </div>

      {viewNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" {...overlayClose(() => setViewNote(null))} data-testid="view-note-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Package size={18} /> Nota #{viewNote.note_number}</h3>
                <p className="text-sm text-slate-500">{viewNote.supplier_name} · {fmtDate(viewNote.issue_date)}</p>
              </div>
              <button onClick={() => setViewNote(null)} className="text-slate-400 hover:text-slate-700" data-testid="close-view-note-btn"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <table className="w-full border border-slate-200 rounded">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Código</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Produto</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Qtd</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Custo Unit.</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(viewNote.items || []).map((it, idx) => {
                    const code = it.code || products.find(p => p.id === it.product_id)?.code || '—';
                    return (
                      <tr key={idx} data-testid={`view-note-item-${idx}`}>
                        <td className="px-3 py-2 text-sm font-mono text-slate-600">{code}</td>
                        <td className="px-3 py-2 text-sm">{it.product_name}{(it.ncm || it.cest) && <span className="block text-[10px] font-mono text-slate-500">NCM {it.ncm || '—'}{it.cest ? ` · CEST ${it.cest}` : ''}</span>}</td>
                        <td className="px-3 py-2 text-right text-sm">{it.quantity}</td>
                        <td className="px-3 py-2 text-right text-sm">R$ {Number(it.unit_cost).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-sm font-bold">R$ {Number(it.subtotal).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 font-bold">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right">TOTAL</td>
                    <td className="px-3 py-2 text-right text-blue-600">R$ {Number(viewNote.total).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
              <p className="text-xs text-slate-400 mt-3">O estoque desses produtos foi atualizado automaticamente ao lançar a nota.</p>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
              {viewNote.status === 'estornada' ? (
                <span className="text-sm font-bold text-red-600 flex items-center gap-1.5" data-testid="note-reversed-info">
                  <X size={16} /> Nota estornada{viewNote.reversed_by_name ? ` por ${viewNote.reversed_by_name}` : ''} — estoque revertido
                </span>
              ) : (
                <span className="text-xs text-slate-500">Estornar reverte o estoque e cancela a conta a pagar vinculada.</span>
              )}
              {viewNote.status !== 'estornada' && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openEdit(viewNote)}
                    className="gap-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                    data-testid="edit-note-btn"
                  >
                    <FileText size={16} /> Editar
                  </Button>
                  <Button
                    type="button"
                    onClick={() => { if (window.confirm(`Confirma o estorno da nota #${viewNote.note_number}? O estoque será revertido e a conta a pagar vinculada será cancelada.`)) reverseNote(viewNote); }}
                    disabled={reversing}
                    className="bg-red-600 hover:bg-red-700 text-white gap-2"
                    data-testid="reverse-note-btn"
                  >
                    <Trash2 size={16} /> {reversing ? 'Estornando...' : 'Estornar Nota'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
